import { Project } from "@/utils/types";
import { useDispatch, useSelector } from "react-redux";
import {
  setCreateType,
  setDeleteProjectCandidateId,
  setEditProjectCandidateId,
  setShowAddEditProjectModal,
  setShowDeleteProjectModal,
} from "@/store/projectsSlice";
import { Folder, InfoIcon, X } from "lucide-react";
import { useCallback, useState } from "react";
import React from "react";
import CanvasKanban from "./CanvasKanban";
import ProjectSettings from "../settings";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import _ from "lodash";
import { getAxiosErorrMessage, getUnexpiredToken } from "@/utils/utils";
import { toast } from "sonner";
import axios from "axios";
import {
  useBuildProjectLink,
  useProjectWithUsers,
} from "@/hooks/useProjectWithUsers";
import { useSupabase } from "../contexts/DatabaseSyncWrapper";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { useNavigate, useSearchParams } from "react-router";
import { ProjectPageHeader } from "./ProjectPageHeader";
import { FilterState } from "./types";
import { ProjectNav } from "./ProjectNav";
import InfoPanel from "../LeftNav/Info";
import useLayoutStore, { ProjectPageNav } from "@/store/zustand/layoutStore";
import UnauthorizedModal from "../UnauthorizedModal";
import { setUnauthorized } from "@/store/codebaseSlice";
import { useUser } from "@clerk/clerk-react";
import { useSession } from "@clerk/clerk-react";

interface ProjectPageProps {}

const ProjectPage: React.FC<ProjectPageProps> = () => {
  const { user } = useUser();
  const { session } = useSession();

  const { triggerRefetchProjects } = useSupabase();

  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();

  const buildLink = useBuildProjectLink();

  const baseProjects = useSelector(
    (state: any) => state.supabaseStore.projects
  );

  const currentProject = useProjectWithUsers();

  const dispatch = useDispatch<any>();

  const deleteProjectCandidateId = useSelector(
    (state: any) => state.projects.deleteProjectCandidateId
  );
  const editProjectCandidateId = useSelector(
    (state: any) => state.projects.editProjectCandidateId
  );
  const showDeleteProjectModal = useSelector(
    (state: any) => state.projects.showDeleteProjectModal
  );
  const showAddEditProjectModal = useSelector(
    (state: any) => state.projects.showAddEditProjectModal
  );

  const [filters, setFilters] = useState<FilterState>({
    userIds: [],
    labels: [],
    search: "",
  });

  const projectPageNav = useLayoutStore((state) => state.projectPageNav);
  const setProjectPageNav = useLayoutStore((state) => state.setProjectPageNav);

  const deleteProjectCandidate = baseProjects?.[deleteProjectCandidateId];

  const closeDeleteProjectModal = () => {
    dispatch(setShowDeleteProjectModal(false));
    dispatch(setDeleteProjectCandidateId(null));
  };

  const unauthorized = useSelector((state: any) => state.codebase.unauthorized);

  const deleteProject = async (user) => {
    const supabaseJWT = await getUnexpiredToken(session);

    axios
      .post(
        `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/projects/${deleteProjectCandidateId}/deleteUser`,
        {},
        {
          headers: {
            Authorization: `Bearer ${supabaseJWT}`,
          },
        }
      )
      .then(() => {
        toast.success("Project deleted successfully");
        closeDeleteProjectModal();
        triggerRefetchProjects();

        // Check if this was the last project and navigate to project creation if so
        // this is different from the first time no projects case where they go to project-create/workflow-select instead
        const remainingProjects = Object.keys(baseProjects || {}).filter(
          (id) => id !== deleteProjectCandidateId
        );
        if (remainingProjects.length === 0) {
          navigate("/project-create/ai-new");
        }
      })
      .catch((err) => {
        toast.error(getAxiosErorrMessage(err));
      });
  };

  const openCanvas = useCallback(
    (project: Project, canvas: any) => {
      navigate(buildLink(project, canvas));
    },
    [buildLink]
  );

  return (
    <div className="flex h-full w-full">
      <div className="flex flex-col items-center gap-1 pl-1 pr-1 pt-1.5 border-r border-secondary-n-900">
        {/* TODO: AI chat button  */}
        {/* <button className="flex justify-center items-center">
                <Icon icon={ICONS.AI_LOGO_MUTED} width={20} height={20} />
              </button>
              <div className="w-5 border-b border-secondary-n-900"></div> */}
        <button
          onClick={() => setProjectPageNav(ProjectPageNav.Projects)}
          className={cn(
            "flex justify-center items-center rounded-md size-8 hover:bg-secondary-n-900",
            projectPageNav === ProjectPageNav.Projects && "!bg-secondary-n-800"
          )}
        >
          <Folder className="size-5" />
        </button>
        <button
          onClick={() => setProjectPageNav(ProjectPageNav.Info)}
          className={cn(
            "flex justify-center items-center rounded-md size-8 hover:bg-secondary-n-900",
            projectPageNav === ProjectPageNav.Info && "!bg-secondary-n-800"
          )}
        >
          <InfoIcon className="size-5" />
        </button>
      </div>
      {!showAddEditProjectModal && (
        <div
          className={cn(
            "w-64 border-r h-full border-secondary-n-900",
            projectPageNav !== ProjectPageNav.Projects && "hidden"
          )}
        >
          <ProjectNav />
        </div>
      )}

      <div
        className={cn(
          "w-64 border-r h-full border-secondary-n-900",
          projectPageNav !== ProjectPageNav.Info && "hidden"
        )}
      >
        <InfoPanel />
      </div>

      <div className="flex-1 flex">
        {showAddEditProjectModal && (
          <div className="w-[300px] min-w-[300px] border-r border-secondary-n-900 h-full flex flex-col">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-secondary-n-900">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-secondary-n-50">
                  {editProjectCandidateId
                    ? baseProjects?.[editProjectCandidateId]?.name
                    : "New Project"}
                </span>
              </div>
              <button
                onClick={() => {
                  if (editProjectCandidateId) {
                    triggerRefetchProjects();
                  }
                  dispatch(setShowAddEditProjectModal(false));
                  dispatch(setEditProjectCandidateId(null));
                  dispatch(setCreateType(null));
                  setProjectPageNav(ProjectPageNav.Projects);
                }}
                className="p-1 hover:bg-secondary-n-900 rounded-md"
              >
                <X className="w-4 h-4 text-secondary-n-400" />
              </button>
            </div>
            <div className="flex-1">
              <ProjectSettings
                editProjectId={editProjectCandidateId}
                editProjectData={
                  editProjectCandidateId
                    ? baseProjects?.[editProjectCandidateId]
                    : null
                }
                scrollAreaHeight="h-[calc(100vh-160px)]"
              />
            </div>
          </div>
        )}

        <div className="pt-4 flex flex-col flex-1 min-w-0">
          <ProjectPageHeader
            project={currentProject}
            filters={filters}
            setFilters={setFilters}
          />
          <div
            className={cn(
              "flex flex-row gap-x-2 items-center px-8 py-4",
              currentProject && "hidden"
            )}
          >
            <Skeleton className="h-6 w-14 bg-secondary-n-900" />
            <Skeleton className="h-4 w-60 bg-secondary-n-900" />
          </div>
          <CanvasKanban
            onSelect={openCanvas}
            project={currentProject}
            filters={filters}
          />
        </div>
      </div>

      <AlertDialog
        open={showDeleteProjectModal}
        onOpenChange={(open: boolean) => {
          if (!open) {
            closeDeleteProjectModal();
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Project</AlertDialogTitle>
            <AlertDialogDescription>
              <div className="w-full flex flex-col">
                <div className="pb-2">
                  Are you sure you want to delete this project?
                </div>
                <div className="flex flex-row gap-1">
                  <div className="text-white font-bold">Project Name:</div>
                  <div className="text-white">
                    {deleteProjectCandidate?.name}
                  </div>
                </div>
                <div className="flex flex-row gap-1">
                  <div className="text-white font-bold">Repo URL:</div>
                  <div className="text-white">
                    {deleteProjectCandidate?.repoPath}
                  </div>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                closeDeleteProjectModal();
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                setSearchParams((prev) => {
                  const newParams = {
                    ...prev,
                  };
                  delete newParams["project_id"];
                  return newParams;
                });
                deleteProject(user);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {unauthorized && (
        <UnauthorizedModal
          onClose={() => {
            dispatch(setUnauthorized(false));
            const fullProjectsList = Object.values(baseProjects || {}) as any[];
            const recentProject =
              fullProjectsList.length > 0 ? fullProjectsList[0] : null;
            if (recentProject) {
              setSearchParams((prev) => ({
                ...prev,
                project_id: recentProject.id,
              }));
            } else {
              navigate("/");
            }
          }}
        />
      )}
    </div>
  );
};

export default ProjectPage;
